import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Small "Copy" button for log/response panels — copies `text` to the
 * clipboard with a brief checkmark confirmation. Falls back to a hidden
 * textarea + execCommand for contexts where the async Clipboard API is
 * blocked (e.g. cross-origin preview iframes). */
export default function CopyButton({ text, label = "Copy", className = "", testId }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const value = typeof text === "string" ? text : JSON.stringify(text, null, 2);
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      try {
        const el = document.createElement("textarea");
        el.value = value;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        // Nothing more we can do — user can still select the text manually.
      }
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className={`h-6 px-2 text-xs gap-1 ${className}`}
      data-testid={testId}
    >
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : label}
    </Button>
  );
}
